import React, { useState } from 'react';
import { Form, Input, Button, Modal, notification } from 'antd';
import { useParams } from 'react-router-dom';
import { FiCheck, FiCheckCircle } from 'react-icons/fi';
import { guardar_encuesta } from '../../../utils/ConexionServidor';
import { ENCUESTAS } from './data';

const ACCENT = '#fccd35';
const PAGE_BG = '#f6f5ef';

// Responsive tweaks that inline styles can't express (media queries).
// Injected once on first import.
const ENCUESTA_STYLE_ID = 'encuesta-responsive-styles';
if (typeof document !== 'undefined' && !document.getElementById(ENCUESTA_STYLE_ID)) {
  const styleEl = document.createElement('style');
  styleEl.id = ENCUESTA_STYLE_ID;
  styleEl.textContent = `
    @media (max-width: 600px) {
      .encuesta-pregunta-header {
        flex-direction: column !important;
        align-items: center !important;
        gap: 10px !important;
        text-align: center;
      }
      .encuesta-pregunta-numero {
        /* override the flex shorthand so the circle stays truly round */
        flex: 0 0 28px !important;
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
        font-size: 13px !important;
        margin-top: 0 !important;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

const renderNota = (nota) => {
  if (!nota) return null;
  if (nota.tipo === 'enlace') {
    return (
      <>
        {nota.texto}{' '}
        <a href={nota.url} target="_blank" rel="noreferrer" style={{ color: '#1677ff' }}>
          {nota.label || nota.url}
        </a>
      </>
    );
  }
  return nota.texto;
};

// Big tap-friendly card-radio. Cleaner than antd's default radios for surveys.
const RadioCard = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            border: selected ? `2px solid ${ACCENT}` : '2px solid #e7e4d8',
            background: selected ? '#fffbe8' : '#fff',
            borderRadius: 10,
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: 16,
            color: '#222',
            fontWeight: selected ? 600 : 400,
            transition: 'all 120ms ease',
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            if (!selected) e.currentTarget.style.borderColor = '#cfc8aa';
          }}
          onMouseLeave={(e) => {
            if (!selected) e.currentTarget.style.borderColor = '#e7e4d8';
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: selected ? `2px solid ${ACCENT}` : '2px solid #cfc8aa',
              background: selected ? ACCENT : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 22px',
              transition: 'all 120ms ease',
            }}
          >
            {selected && <FiCheck size={14} color="#000" strokeWidth={3} />}
          </span>
          <span>{opt}</span>
        </button>
      );
    })}
  </div>
);

const Encuesta = () => {
  const { identificador } = useParams();
  const encuesta = ENCUESTAS[identificador];
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // Two-step flow: answer questions → modal asks for contact info → send.
  const [respuestas, setRespuestas] = useState(null);
  const [datosForm] = Form.useForm();

  const formattedAnswers = (questionAnswers) => {
    if (!encuesta || !questionAnswers) return '';
    return encuesta.preguntas
      .map((p, i) => `P${i + 1}. ${p.texto}\nR: ${questionAnswers[p.id] || '(sin respuesta)'}`)
      .join('\n\n');
  };

  // Step 1: questions submitted → open the contact modal.
  const onPreguntasSubmit = (values) => {
    setRespuestas(values);
  };

  const closeModal = () => {
    if (enviando) return;
    setRespuestas(null);
    datosForm.resetFields();
  };

  // Step 2: contact form submitted → send everything.
  const onDatosSubmit = async (datos) => {
    if (enviando || !respuestas) return;
    setEnviando(true);

    const payload = {
      encuesta_id: identificador,
      respuestas: encuesta.preguntas.reduce((acc, p) => {
        acc[p.id] = respuestas[p.id] || null;
        return acc;
      }, {}),
      nombre: (datos.nombre && datos.nombre.trim()) || null,
      apellido: (datos.apellido && datos.apellido.trim()) || null,
      empresa: (datos.empresa && datos.empresa.trim()) || null,
      email: (datos.email && datos.email.trim()) || null,
      comentarios: (datos.comentarios && datos.comentarios.trim()) || null,
      resumen: formattedAnswers(respuestas),
    };

    try {
      const res = await guardar_encuesta(payload);
      if (res && res.success) {
        setEnviado(true);
      } else {
        throw new Error('Servidor rechazó la respuesta');
      }
    } catch (err) {
      notification.error({
        message: 'No se pudo enviar la encuesta',
        description: 'Por favor inténtalo de nuevo en unos segundos.',
        duration: 8,
      });
    } finally {
      setEnviando(false);
    }
  };

  // — Estados secundarios —————————————————————————————————————

  if (!encuesta) {
    return (
      <div style={{ background: PAGE_BG, padding: '60px 20px 100px', minHeight: '60vh' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '40px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h2 style={{ color: '#333', marginBottom: 8 }}>Encuesta no disponible</h2>
          <p style={{ color: '#777', margin: 0 }}>
            La encuesta solicitada no existe o ya cerró. Comunícate con CANIMOLT
            para más información.
          </p>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div style={{ background: PAGE_BG, padding: '60px 20px 120px', minHeight: '60vh' }}>
        <div style={{
          maxWidth: 560, margin: '0 auto', textAlign: 'center',
          padding: '48px 24px', background: '#fff', borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: ACCENT, margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiCheckCircle size={40} color="#000" strokeWidth={2.5} />
          </div>
          <h2 style={{ color: '#222', marginBottom: 12, fontSize: 26 }}>
            ¡Gracias por participar!
          </h2>
          <p style={{ color: '#666', maxWidth: 420, margin: '0 auto', fontSize: 15, lineHeight: 1.5 }}>
            Tu respuesta fue registrada. Apreciamos que hayas tomado un momento
            para ayudarnos.
          </p>
        </div>
      </div>
    );
  }

  // — Form ——————————————————————————————————————————————————————

  return (
    <div style={{ background: PAGE_BG, padding: '32px 16px 120px', minHeight: '70vh' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        {/* Header card */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '28px 28px 26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 16,
        }}>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 1,
            background: ACCENT, color: '#000', padding: '5px 12px', borderRadius: 4,
            marginBottom: 16,
          }}>
            BREVE ENCUESTA · CANIMOLT
          </div>
          <p style={{ color: '#444', fontSize: 16, lineHeight: 1.55, margin: 0 }}>
            {encuesta.intro}
          </p>
        </div>

        <Form layout="vertical" onFinish={onPreguntasSubmit}>
          {/* Question cards */}
          {encuesta.preguntas.map((p, i) => (
            <div
              key={p.id}
              style={{
                background: '#fff', borderRadius: 12, padding: '22px 24px 18px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 14,
              }}
            >
              <div
                className="encuesta-pregunta-header"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}
              >
                <div
                  className="encuesta-pregunta-numero"
                  style={{
                    flex: '0 0 32px', width: 32, height: 32, borderRadius: '50%',
                    background: '#f3edd0', color: '#7a6622', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, marginTop: 2,
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#222', lineHeight: 1.45 }}>
                    {p.texto}
                  </div>
                  {p.nota && (
                    <div style={{ fontSize: 13, color: '#666', marginTop: 6, lineHeight: 1.45 }}>
                      {renderNota(p.nota)}
                    </div>
                  )}
                </div>
              </div>
              <Form.Item
                name={p.id}
                rules={[{ required: true, message: 'Por favor selecciona una opción' }]}
                style={{ marginBottom: 0 }}
              >
                <RadioCardField options={p.opciones} />
              </Form.Item>
            </div>
          ))}

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{
                minWidth: 260, height: 52,
                background: ACCENT, borderColor: ACCENT,
                color: '#000', fontWeight: 700, fontSize: 16,
                borderRadius: 10, boxShadow: '0 4px 14px rgba(252,205,53,0.4)',
              }}
            >
              Enviar respuestas
            </Button>
          </div>
        </Form>
      </div>

      {/* Step 2: contact info modal */}
      <Modal
        title={null}
        visible={!!respuestas}
        open={!!respuestas}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnClose
        maskClosable={!enviando}
        closable={!enviando}
        width={440}
        bodyStyle={{ padding: 28 }}
      >
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 20, color: '#222', fontWeight: 700 }}>
            Un último paso
          </h3>
          <p style={{ margin: 0, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
            Déjanos tus datos para registrar tu respuesta.
          </p>
        </div>
        <Form
          form={datosForm}
          layout="vertical"
          onFinish={onDatosSubmit}
          requiredMark={false}
        >
          <Form.Item
            label="Nombre"
            name="nombre"
            rules={[{ required: true, message: 'Ingresa tu nombre' }]}
          >
            <Input size="large" autoComplete="given-name" autoFocus />
          </Form.Item>
          <Form.Item
            label="Apellido"
            name="apellido"
            rules={[{ required: true, message: 'Ingresa tu apellido' }]}
          >
            <Input size="large" autoComplete="family-name" />
          </Form.Item>
          <Form.Item
            label="Correo"
            name="email"
            rules={[
              { required: true, message: 'Ingresa tu correo' },
              { type: 'email', message: 'Correo no válido' },
            ]}
          >
            <Input size="large" autoComplete="email" inputMode="email" />
          </Form.Item>
          <Form.Item
            label="Empresa"
            name="empresa"
            rules={[{ required: true, message: 'Ingresa tu empresa' }]}
          >
            <Input size="large" autoComplete="organization" />
          </Form.Item>
          <Form.Item
            label="Comentarios (opcional)"
            name="comentarios"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={enviando}
              style={{
                height: 48,
                background: ACCENT, borderColor: ACCENT,
                color: '#000', fontWeight: 700, fontSize: 15,
                borderRadius: 10,
              }}
            >
              {enviando ? 'Enviando…' : 'Enviar encuesta'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Wrapper so antd Form.Item can bind value/onChange to our custom RadioCard.
const RadioCardField = ({ value, onChange, options }) => (
  <RadioCard options={options} value={value} onChange={onChange} />
);

export default Encuesta;
